<script>
import { prettyURL } from '@/modules/prettifier';

export default {
  name: 'SiteCard',
  props: {
    site: Object
  },
  data() {
    return {
      isScreenshotPending: false,
      screenshotTimeout: null,
      screenshotRetries: 0
    };
  },
  computed: {
    prettyURL() {
      return prettyURL(this.site.url);
    }
  },
  mounted() {
    // If there's no screenshot, start polling for it
    if (!this.site.screenshot) {
      this.pollForScreenshot();
    }
  },
  beforeDestroy() {
    clearTimeout(this.screenshotTimeout);
  },
  methods: {
    // Polls the site until the screenshot updates or max retries is reached
    pollForScreenshot() {
      this.isScreenshotPending = true;
      this.screenshotTimeout = setTimeout(() => {
        this.$http
          .get(`/sites/${this.site.id}`)
          .then(res => {
            const site = res.data.site;
            if (site.screenshot) {
              this.$emit('screenshot-ready', site.screenshot);
            } else {
              if (++this.screenshotRetries < 3) {
                this.pollForScreenshot();
              } else {
                this.isScreenshotPending = false;
              }
            }
          })
          .catch(() => (this.isScreenshotPending = false));
      }, 5000);
    }
  }
};
</script>

<template>
  <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="8">
    <div class="site-card">
      <!-- Ribbon -->
      <div v-if="site.isDemo" class="site-ribbon">
        {{ $t('Demo Site') }}
      </div>

      <!-- Header -->
      <div class="site-card-header">
        <router-link :to="{ name: 'Site', params: { siteId: site.id } }">
          <div
            v-loading="!site.screenshot"
            :style="site.screenshot ? { 'background-image': `url('${site.screenshot}')` } : {}"
            class="site-card-screenshot"
          />
        </router-link>
      </div>

      <!-- Footer -->
      <div class="site-card-footer">
        <div class="site-card-name">
          <router-link :to="{ name: 'Site', params: { siteId: site.id } }">
            <i v-if="site.isLocked" class="el-icon-fa-lock" />
            {{ site.name }}
          </router-link>
        </div>
        <div class="site-card-url text-truncate">
          <a :href="site.url" rel="noopener" target="_blank">
            {{ prettyURL }}
            <i class="el-icon-fa-external-link" />
          </a>
        </div>
        <div v-if="$store.state.user.canManageSites" class="site-card-settings">
          <!-- Edit (all except demos) -->
          <router-link
            v-if="!site.isDemo"
            :to="{ name: 'EditSite', params: { siteId: site.id } }"
            :title="$t('Edit Site')"
          >
            <i class="el-icon-fa-cog" />
          </router-link>

          <!-- Delete (only demos) -->
          <a v-else :title="$t('Delete')" href="#" @click.prevent="$emit('delete', site)">
            <i class="el-icon-fa-trash" />
          </a>
        </div>
      </div>
    </div>
  </el-col>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.site-card {
  position: relative;
  margin-bottom: 2rem;
  user-select: none;
  border-radius: 0.25rem;
  box-shadow: $--box-shadow-small;
  overflow: hidden;

  @include breakpoint('sm-and-down') {
    margin-bottom: 1rem;
  }
}

.site-ribbon {
  position: absolute;
  z-index: $--index-popper + 1; // show above .el-loading-mask
  width: 200px;
  top: 25px;
  left: -50px;
  text-align: center;
  transform: rotate(-45deg);
  font-size: $--font-size-small;
  border-top-right-radius: 0.125rem;
  border-bottom-right-radius: 0.125rem;
  background-color: $--color-success;
  color: $--color-white;
  padding: 0.6rem 1rem;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 0;
    width: 100%;
    border-top: dashed 1px rgba(white, 0.3);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 0;
    width: 100%;
    border-top: dashed 1px rgba(white, 0.3);
  }
}

.site-card-header {
  .site-card-screenshot {
    position: relative;
    padding-top: 62.5%;
    background-color: $--color-white;
    background-size: cover;
    border-bottom: solid 1px darken($--color-gray-10, 2.5%);
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }
}

.site-card-footer {
  background-color: $--color-white;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  padding: 1rem 1.25rem;

  .site-card-name {
    margin-bottom: 0.25rem;
  }

  .site-card-url {
    font-size: $--font-size-small;
    color: $--color-gray-40;
    margin-right: 1.5rem;

    a {
      color: $--color-gray-40;
    }

    a:hover {
      color: darken($--color-gray-40, 7.5%);
    }

    i {
      visibility: hidden;
    }

    a:hover i {
      visibility: visible;
    }
  }

  .site-card-settings {
    position: absolute;
    right: 1.25rem;
    bottom: 1rem;
    font-size: 1.1rem;

    a {
      color: $--color-gray-40;
    }

    a:hover {
      color: darken($--color-gray-40, 7.5%);
    }
  }
}
</style>
